import { getActiveAuditTrail, LineItemDiffPath } from "@rebuild/shared";
import type { LineItemDiff } from "@rebuild/shared";

export type AgentCreatedDiff = Extract<LineItemDiff, { source: "estimate_agent" }>;

export const findAgentCreatedDiff = (auditTrail: LineItemDiff[] | undefined): AgentCreatedDiff | undefined =>
  getActiveAuditTrail(auditTrail ?? []).find(
    (diff): diff is AgentCreatedDiff => diff.source === "estimate_agent" && diff.diff.path === LineItemDiffPath.LineItemCreated,
  );

const FIELD_DISPLAY_NAMES: Record<LineItemDiffPath, string> = {
  [LineItemDiffPath.Description]: "description",
  [LineItemDiffPath.LineItemId]: "line item ID",
  [LineItemDiffPath.Confirmed]: "confirmed",
  [LineItemDiffPath.AddedByTPARule]: "added by TPA rule",
  [LineItemDiffPath.RemovedByTPARule]: "is excluded",
  [LineItemDiffPath.Comment]: "comment",
  [LineItemDiffPath.Formula]: "formula",
  [LineItemDiffPath.RoomName]: "room name",
  [LineItemDiffPath.IssueExplanation]: "issue explanation",
  [LineItemDiffPath.Confidence]: "confidence",
  [LineItemDiffPath.HasError]: "is issue",
  [LineItemDiffPath.Archived]: "is archived",
  [LineItemDiffPath.IsLikely]: "is recommendation",
  [LineItemDiffPath.IsComplement]: "is complement",
  [LineItemDiffPath.IsMutuallyExcluded]: "is mutually excluded",
  [LineItemDiffPath.LineItemCreated]: "",
};

const getSourceName = (entry: LineItemDiff): string => {
  switch (entry.source) {
    case "rules_engine":
      return `rule "${entry.ruleName}"`;
    case "general_room_processing":
      return "general-room-processing";
    case "user_edits":
      return entry.userFirstName ? `user ${entry.userFirstName} ${entry.userLastName ?? ""}`.trim() : "User";
    case "check_for_issues":
      return "issue-checker";
    case "convert_countertop_units":
      return "convert-countertop-units";
    case "no_remove_and_replace":
      return "no-remove-and-replace";
    case "reversal":
      return "reversal";
    case "estimate_agent":
      return entry.acceptedByFirstName ? `estimate agent (accepted by ${`${entry.acceptedByFirstName} ${entry.acceptedByLastName ?? ""}`.trim()})` : "estimate agent";
  }
};

export const formatDiffDescription = (entry: LineItemDiff): string => {
  const sourceName = getSourceName(entry);
  const fieldName = FIELD_DISPLAY_NAMES[entry.diff.path];

  if (entry.diff.type === "added" && entry.diff.path === LineItemDiffPath.LineItemCreated) {
    return `Added by ${sourceName}`;
  }

  return `"${fieldName}" updated to "${entry.diff.newValue}" by ${sourceName}`;
};
